import toast, { Toaster } from "react-hot-toast";
import SwitchLanguageCard from "../../../components/card/SwitchLanguageCard";
import { cn } from "../../../utils/cn";
import { useTranslation } from "react-i18next";
import { APP_IMAGES } from "../../../constants/images";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../../components/button";
import { APP_ROUTES, AUTH_ROUTES, ROUTE_NAMES } from "../../../routes";
import { useLocation, useNavigate } from "react-router-dom";
import OTPInput from "../../../components/inputs/OTPInput";
import {
  useGetUserPrivileges,
  useResendOTP,
  useVerifyOTP,
} from "../../../hooks/user/mutation";
import Loader from "../../../components/loader";
import { OTP_LENGTH, USER_ROLES_IDS } from "../../../constants/common";
import { DecodedUser } from "../../../hooks/user/interface";
import { jwtDecode } from "jwt-decode";
import useAuthStore from "../../../store/Auth";
import { RolePrivilege } from "../../../store/Auth/interface";

const OTP = () => {
  const { i18n, t } = useTranslation();

  const [otp, setOtp] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const { setUserRoles, setIsLoggedIn } = useAuthStore();

  const { mutateAsync: VerifyOTP, isPending: isVerifyOTPLoading } =
    useVerifyOTP();

  const { mutateAsync: ResendOTP, isPending: isResendOTPLoading } =
    useResendOTP();

  const userDetails = useMemo(
    () => ({
      username: location.state?.username,
      password: location.state?.password,
    }),
    [location.state?.username, location.state?.password]
  );

  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    const lastResend = localStorage.getItem("otpLastResend");
    if (lastResend) {
      const elapsed = Math.floor((Date.now() - Number(lastResend)) / 1000);
      const remaining = 30 - elapsed;
      if (remaining > 0) {
        setRemainingTime(remaining);
      }
    }
  }, []);

  useEffect(() => {
    if (remainingTime > 0) {
      const interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [remainingTime]);

  const handleResend = useCallback(async () => {
    if (remainingTime > 0) return;

    try {
      await ResendOTP(userDetails);
      toast.success(t("otpResentSuccessfully"));
    } catch (error) {
    } finally {
      const now = Date.now();
      localStorage.setItem("otpLastResend", now.toString());
      setRemainingTime(30);
    }
  }, [ResendOTP, remainingTime, t, userDetails]);

  const { mutateAsync: GetUserPrivileges } = useGetUserPrivileges();

  const verifyOTP = useCallback(async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error(t("invalidOTP"));
      return;
    }

    try {
      const body = {
        ...userDetails,
        otpCode: otp,
      };

      const res = await VerifyOTP(body);

      if (res?.token) {
        const decodedUser: DecodedUser = jwtDecode(res?.token);

        if (decodedUser?.RoleId) {
          const privileges = await GetUserPrivileges();

          setUserRoles(privileges);

          if (res?.token) {
            setIsLoggedIn(true);
          }

          if (Object.keys(privileges).length > 0) {
            const isHomePrivilege = privileges?.rolePrivileges?.some(
              (privilege: RolePrivilege) =>
                privilege.s_RouteName === ROUTE_NAMES.HOME_SEARCH
            );

            const isApproveVisitRequestsPrivilege =
              privileges?.rolePrivileges?.some(
                (privilege: RolePrivilege) =>
                  privilege.s_RouteName ===
                  ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH
              );

            const isApprovePermitRequestsPrivilege =
              privileges?.rolePrivileges?.some(
                (privilege: RolePrivilege) =>
                  privilege.s_RouteName ===
                  ROUTE_NAMES.APPROVE_PERMIT_REQUEST_SEARCH
              );

            if (
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.ADMIN ||
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.SECURITY ||
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.SECURITY_MANAGER ||
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.RECEPTION
            ) {
              navigate(
                isHomePrivilege ? APP_ROUTES.HOME : APP_ROUTES.UNAUTHORIZED
              );
            } else if (
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.DIRECT_MANAGER ||
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.INVENTORY_MANAGER
            ) {
              navigate(
                isApprovePermitRequestsPrivilege
                  ? APP_ROUTES.APPROVE_PERMIT_REQUEST
                  : APP_ROUTES.UNAUTHORIZED
              );
            } else {
              navigate(
                isApproveVisitRequestsPrivilege
                  ? APP_ROUTES.APPROVE_VISIT_REQUEST
                  : APP_ROUTES.UNAUTHORIZED
              );
            }
          }
        }
      }
    } catch (error: any) {
      if (error.result?.isOTPExpired) {
        toast.error(t("invalidExpiredOtpTryAgain"));
        return;
      }
      toast.error(t("somethingWentWrong"));
    }
  }, [
    GetUserPrivileges,
    VerifyOTP,
    navigate,
    otp,
    setIsLoggedIn,
    setUserRoles,
    t,
    userDetails,
  ]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <SwitchLanguageCard
        styles={cn(
          "absolute top-1 sm:top-2 max-w-max bg-white",
          i18n.language === "en" ? "right-2" : "left-2"
        )}
      />
      <div className="bg-purple-primary/10 dark:bg-purple-primary/40 dark:text-black">
        <section className="sm:min-w-[60vw] md:min-w-[430px] xl:w-1/4 xl:mx-auto flex flex-col md:flex-row justify-center items-center py-8 md:py-16 mx-6 md:mx-0 h-dvh">
          <div className="flex flex-col p-5 gap-5 bg-white rounded-2xl">
            <div>
              <img
                className="rounded-none mx-auto w-40 lg:w-44 object-contain"
                src={APP_IMAGES.logo}
                alt=""
              />
            </div>

            <div className="flex items-center flex-col gap-8">
              <div className="flex flex-col gap-2 items-center justify-center">
                <h1 className="font-semibold text-xl md:text-2xl text-center ">
                  {t("otpVerification")}
                </h1>

                <h2 className="text-center text-sm sm:text-base">
                  {t("enterOTP")}
                </h2>
              </div>

              <div className="flex flex-col gap-2 w-full">
                <div className="mx-auto">
                  <OTPInput value={otp} onChange={setOtp} />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-center gap-2 w-full">
                <Button
                  className="w-full sm:w-[180px]"
                  disabled={otp.length !== OTP_LENGTH}
                  isLoading={isVerifyOTPLoading}
                  onClick={verifyOTP}
                >
                  {t("verify")}
                </Button>
                <Button
                  onClick={() => navigate(AUTH_ROUTES.LOGIN)}
                  className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-trw-fullansparent sm:w-[180px]"
                >
                  {t("goToLogin")}
                </Button>
              </div>

              <p className="text-sm -mt-4 flex items-center gap-2">
                {t("didntGetCode")}{" "}
                <span
                  onClick={handleResend}
                  className={cn(
                    "font-semibold",
                    remainingTime > 0 || isResendOTPLoading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-purple-primary underline cursor-pointer"
                  )}
                >
                  {remainingTime > 0
                    ? `${t("resend")} (${remainingTime}s)`
                    : t("resend")}
                </span>
                {isResendOTPLoading && <Loader size={14} />}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OTP;
